import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { NgTimedAnimationService } from '../../shared/services/timed-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationEffectOptions, INgAnimationStates } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function pop(scale: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	scale = scale ? scale : 1.5;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'scale3d(1, 1, 1)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `scale3d(${scale}, ${scale}, ${scale})`,
				easing 		: 'ease',
				offset 		: 0.5
			}),
			style({
				transform 	: 'scale3d(1, 1, 1)',
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Pop on Event
 */
@Directive({
    selector: '[ngPopOnEvent]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgPopOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.effectOptions?.scale && this.effectOptions?.scale < 0) throw new Error(`PopOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.effectOptions?.scale}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pop(this.effectOptions?.scale),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


/**------------------------------------------------------
 * Pop on Mouse Hover
 */
@Directive({
    selector: '[ngPopOnMouseHover]',
    providers: [NgTimedAnimationService],
    standalone: false
})
export class NgPopOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomAnimationEffectOptions> = null!;
	@Input() targetElement: HTMLElement 						   = null!;

	constructor(
		private elementRef 				: ElementRef,
		private timedAnimationService 	: NgTimedAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale value is relative to the animation functionality
		if (this.effectOptions?.scale && this.effectOptions?.scale < 0) throw new Error(`PopOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be a positive number: ${this.effectOptions?.scale}`);

		//1 - setup the animation
		this.timedAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: pop(this.effectOptions?.scale),
			event			: EnumNgEventName.MouseHover,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay,
				timeout		: this.effectOptions?.timeout || this.effectOptions?.duration
			}
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomAnimationEffectOptions extends INgAnimationEffectOptions {
	scale : number;
}
